import { defineStore } from "pinia";
import { sha1 } from "object-hash";
import { FetchMethods } from "@src/composables/api";
import _omit from "lodash/omit";

export interface FetchResponse {
  status: Response["status"];
  data: any;
}

export const useApiStore = defineStore("ApiStore", {
  // state
  state: () => {
    return {
      requests: {} as Record<string, Promise<FetchResponse>>,
      cache: {} as Record<string, FetchResponse["data"]>,
    };
  },

  actions: {
    doFetch(url: string, init: RequestInit, useCache: boolean) {
      // Create hash. NB: Omit 'signal' otherwise AbortController breaks
      const hash = sha1({ ..._omit(init, ["signal"]), url });
      // Set 'isGetRequest' boolean
      const isGetRequest = init.method === FetchMethods.GET;

      // 1st: Get existing request promise
      let reqPromise = this.requests[hash];
      // For GET requests – return existing promise (if found)
      if (isGetRequest && !!reqPromise) return reqPromise;

      // 2nd: Get cached result (if any)
      const cachedData = this.getFromCache(hash);
      // For GET requests – return cached result (if found)
      if (isGetRequest && useCache && !!cachedData) return { data: cachedData };

      // Otherwise, create new fetch promise
      reqPromise = fetch(url, init)
        .then(async (response) => {
          // Digest response data (JSON)
          const data = await response.json();
          // Unpack response object
          const { ok, status } = response;
          // If response was not OK, reject promise
          if (!ok) return Promise.reject({ status, data });
          // Otherwise, cache the GET response data
          if (isGetRequest && useCache) this.putToCache(hash, data);
          // Resolve the promise
          return Promise.resolve({ status, data });
        })
        // Finally, delete request promise
        .finally(() => delete this.requests[hash]);
      // Save request promise
      if (isGetRequest) this.requests[hash] = reqPromise;
      // Return promise
      return reqPromise;
    },

    getFromCache(hash: string) {
      // Get cached data
      const cache = this.cache[hash];
      // Return from cache if within __maxage
      if (cache?.__maxage >= new Date()) return _omit(cache, ["__maxage"]);
      // Otherwise delete cache
      if (cache) delete this.cache[hash];
      // Return undefined
      return undefined;
    },

    putToCache(hash: string, data: any) {
      this.cache[hash] = { ...data, __maxage: this.getMaxAge() };
    },

    getMaxAge(seconds = 60) {
      const now = new Date();
      const maxAge = now.setSeconds(now.getSeconds() + seconds);
      return maxAge;
    },
  },
});
